import type { PrimitiveType, PrimitiveTypeName } from '#layers/data-model/types/schema/primitive-type';
import { isPrimitiveTypeFloat, isPrimitiveTypeInteger } from '#layers/data-model/utils/schema/primitive-type';

/** What a float64 round-trip costs: any fewer and the formatter silently truncates the value. */
const MAX_SIGNIFICANT_DIGITS = 21;

interface NumberInputOptions {
	formatOptions: Intl.NumberFormatOptions;
	stepSnapping: boolean;
}

/**
 * Number input behaviour a primitive type dictates, independent of any per-field configuration.
 *
 * The underlying number field commits a value by running it through its formatter, so the format
 * options are what decide the precision that survives: left at their defaults a float loses
 * everything past three decimals.
 *
 * Integers stay whole through step snapping rather than through format options, because a
 * zero-fraction-digit formatter also drives the field's keystroke validator, which then rejects the
 * decimal point outright — typing `1.5` would silently commit `15`.
 *
 * Returns `undefined` for types that are not backed by a number input (`decimal` included, which
 * carries arbitrary precision as a string).
 */
export function getNumberInputOptions(type: PrimitiveType | PrimitiveTypeName): NumberInputOptions | undefined {
	if (isPrimitiveTypeInteger(type)) {
		return { formatOptions: {}, stepSnapping: true };
	}

	if (isPrimitiveTypeFloat(type)) {
		return { formatOptions: { maximumSignificantDigits: MAX_SIGNIFICANT_DIGITS }, stepSnapping: false };
	}

	return undefined;
}
